import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, groupBy, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { CAMPAIGN_TYPES, wizardEntities } from 'containers/AwarenessCenter/constants'

import { selectDefaultLocaleId, selectSelectedTrainingVideo } from '../selectors'

import {
  createCustomTrainingError,
  createCustomTrainingSuccess,
  fetchTrainingVideosError,
  fetchTrainingVideosSuccess,
  uploadCustomVideoSuccess,
  uploadVideoLinkError,
} from './actions'
import {
  CREATE_CUSTOM_TRAINING_ASYNC,
  CUSTOM_MODULE_IMAGE_PREVIEW,
  CUSTOM_MODULE_NAME_URL,
  CUSTOM_MODULE_TYPES,
  FETCH_TRAINING_VIDEOS_ASYNC,
  SOURCE_VALUES,
  TRAINING_VIDEO_TYPES,
  UPLOAD_VIDEO_LINK_ASYNC,
} from './constants'

const fetchTrainingVideosEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_TRAINING_VIDEOS_ASYNC.INITIATE),
    filter(({ payload }) => payload.vendor !== SOURCE_VALUES.CUSTOM_MODULE),
    // Latest request per vendor wins; vendors load side by side for the multi-provider catalog.
    groupBy(({ payload }) => payload.vendor),
    mergeMap((vendor$) =>
      vendor$.pipe(
        switchMap((action) => {
          const defaultLocaleId = selectDefaultLocaleId(state$.value)
          const selectedLocaleIds =
            state$.value.awarenessCenter[wizardEntities[CAMPAIGN_TYPES.SIMULATION]]?.self?.campaign
              ?.selectedLocaleIds || []
          const localeIds = selectedLocaleIds.includes(defaultLocaleId)
            ? selectedLocaleIds
            : [defaultLocaleId, ...selectedLocaleIds]

          return api.wizardCampaign
            .getTrainingVideos(
              state$.value.partnerOptions.selectedCompany?.value,
              action.payload.vendor,
              localeIds
            )
            .pipe(
              map((response) => fetchTrainingVideosSuccess(response, action.payload.vendor)),
              catchError((error) => of(epicsErrorHandler(fetchTrainingVideosError, error, [400]))),
              takeUntil(action$.pipe(ofType(FETCH_TRAINING_VIDEOS_ASYNC.CANCEL)))
            )
        })
      )
    )
  )

const uploadVideoLinkEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPLOAD_VIDEO_LINK_ASYNC.INITIATE),
    mergeMap((action) =>
      api.wizardCampaign
        .uploadVideoLink(
          action.payload.url,
          action.payload.localeId,
          action.payload.trainingId || null
        )
        .pipe(
          mergeMap(({ response = {} }) => {
            const selectedTrainingVideo = selectSelectedTrainingVideo(state$.value)
            const isCustomToVendor =
              selectedTrainingVideo[0]?.vendor === SOURCE_VALUES.CUSTOM_MODULE
            const items = isCustomToVendor ? selectedTrainingVideo[0]?.items || [] : []
            const curLocaleIndex = items.findIndex(
              ({ localeId }) => localeId === action.payload.localeId
            )
            const localeItem = {
              localeId: action.payload.localeId,
              imagePreview: CUSTOM_MODULE_IMAGE_PREVIEW,
              content: action.payload.url,
              customType: CUSTOM_MODULE_TYPES.CUSTOM_URL,
            }

            if (curLocaleIndex >= 0) {
              items[curLocaleIndex] = localeItem
            } else {
              items.push(localeItem)
            }

            return of(
              uploadCustomVideoSuccess({
                id: response.id,
                name: CUSTOM_MODULE_NAME_URL,
                vendor: SOURCE_VALUES.CUSTOM_MODULE,
                type: TRAINING_VIDEO_TYPES.CUSTOM_CONTENT.value,
                items,
              })
            )
          }),
          catchError((error) => of(epicsErrorHandler(uploadVideoLinkError, error, [400])))
        )
    )
  )

const createCustomTrainingEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_CUSTOM_TRAINING_ASYNC.INITIATE),
    mergeMap(() =>
      api.wizardCampaign.createCustomTraining().pipe(
        mergeMap(({ response = {} }) => of(createCustomTrainingSuccess(response.id))),
        catchError((error) => of(epicsErrorHandler(createCustomTrainingError, error, [400])))
      )
    )
  )

export default combineEpics(fetchTrainingVideosEpic, uploadVideoLinkEpic, createCustomTrainingEpic)
